import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronDown, Building2, Check, Shield, Crown, User, Settings, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/app/providers';
import { UserAffiliation } from '@/models/userAffiliation';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { useOrganizationChange } from '@/contexts/OrganizationChangeContext';
import { useTheme } from '@/shared/contexts/ThemeContext';

interface OrganizationWithInfo extends UserAffiliation {
  idorga: string;
  organizationInfo?: {
    id: string;
    name: string;
    slug: string;
    description?: string;
    logo?: string;
  };
}

interface OrganizationSwitcherProps {
  className?: string;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { idorga: currentOrgId } = useParams<{ idorga: string }>();
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  // Utiliser les affiliations de usePermissions via AuthContext au lieu de useUserAffiliation
  // pour éviter les appels manuels et les boucles infinies
  const { affiliations, permissionsInitialized, isAdmin } = useAuth();
  const loading = !permissionsInitialized;

  // Utiliser idorga au lieu de organizationId dans les affiliations
  // Utiliser une référence pour éviter les re-renders inutiles
  const affiliationsWithIdorga = React.useMemo(() => {
    const mapped = affiliations.map(aff => ({
      ...aff,
      idorga: aff.organizationId
    }));
    return mapped;
  }, [affiliations]);

  // Créer une clé de référence basée sur les IDs des affiliations pour détecter les changements réels
  const affiliationsKey = React.useMemo(() =>
    affiliations.map(aff => aff.id).join(','),
    [affiliations]
  );

  // Extraire l'organisation depuis l'URL (ex: /orga/sentiweb/admin -> sentiweb)
  const currentOrganizationId = React.useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const orgaIndex = pathSegments.indexOf('orga');
    if (orgaIndex !== -1 && pathSegments[orgaIndex + 1]) {
      return pathSegments[orgaIndex + 1];
    }
    return currentOrgId || ''; // Fallback sur useParams
  }, [location.pathname, currentOrgId]);

  const [isOpen, setIsOpen] = useState(false);
  const [organizationsWithInfo, setOrganizationsWithInfo] = useState<OrganizationWithInfo[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const { setIsChanging } = useOrganizationChange();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0 });

  // Les affiliations sont chargées automatiquement par usePermissions via AuthContext
  // Pas besoin d'appeler getUserOrganizations() manuellement

  // Calculer la position du dropdown quand il s'ouvre
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + 8, // 8px = mt-2
        left: buttonRect.left
      });
    }
  }, [isOpen]);

  // Fermer le dropdown avec la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Charger les informations des organisations
  // Utiliser une référence précédente pour éviter les rechargements inutiles
  const prevAffiliationsKeyRef = React.useRef<string>('');

  useEffect(() => {
    // Si on est toujours en train de charger les affiliations, ne rien faire
    if (loading) {
      return;
    }

    // Si les affiliations n'ont pas changé, ne pas recharger
    if (affiliationsKey === prevAffiliationsKeyRef.current) {
      return;
    }

    // Si pas d'affiliations, réinitialiser
    if (!affiliationsWithIdorga.length) {
      setOrganizationsWithInfo([]);
      setLoadingOrgs(false);
      prevAffiliationsKeyRef.current = affiliationsKey;
      return;
    }

    // Mettre à jour la référence avant de charger
    prevAffiliationsKeyRef.current = affiliationsKey;

    const loadOrganizationsInfo = async () => {
      setLoadingOrgs(true);
      try {
        // Charger les informations détaillées des organisations depuis Firestore
        const orgsWithInfo: OrganizationWithInfo[] = await Promise.all(
          affiliationsWithIdorga.map(async (affiliation) => {
            try {
              // Charger les infos de l'organisation depuis Firestore
              const orgConfig = await OrgaConfigurationRepository.getByOrganizationId(affiliation.idorga);

              return {
                ...affiliation,
                idorga: affiliation.idorga,
                organizationInfo: {
                  id: affiliation.idorga,
                  name: orgConfig?.name || affiliation.idorga,
                  slug: affiliation.idorga,
                  description: orgConfig?.description || '',
                  logo: orgConfig?.personalization?.logo || orgConfig?.logo || '',
                }
              };
            } catch (error) {
              console.error('Erreur lors du chargement de l\'organisation:', affiliation.idorga, error);
              return {
                ...affiliation,
                idorga: affiliation.idorga,
                organizationInfo: {
                  id: affiliation.idorga,
                  name: affiliation.idorga,
                  slug: affiliation.idorga,
                  logo: '',
                }
              };
            }
          })
        );

        setOrganizationsWithInfo(orgsWithInfo);
      } catch (error) {
        console.error('Erreur lors du chargement des organisations:', error);
        setOrganizationsWithInfo([]);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizationsInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliationsKey, loading]); // Inclure loading pour vérifier quand le chargement est terminé

  // Utiliser toutes les organisations affiliées (pas seulement celles où l'utilisateur est admin)
  const allOrganizations = organizationsWithInfo;

  const currentOrganization = React.useMemo(() =>
    allOrganizations.find(org => org.idorga === currentOrganizationId),
    [allOrganizations, currentOrganizationId]
  );

  // Fonction pour obtenir l'icône de rôle
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4" style={{ color: '#D97706' }} />;
      case 'admin':
        return <Shield className="h-4 w-4" style={{ color: theme.colors.primary || '#2563EB' }} />;
      default:
        return <User className="h-4 w-4" style={{ color: '#4B5563' }} />;
    }
  };

  // Fonction pour obtenir le label de rôle
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('organizations.switcher.roles.owner');
      case 'admin':
        return t('organizations.switcher.roles.admin');
      case 'member':
        return t('organizations.switcher.roles.member');
      case 'viewer':
        return t('organizations.switcher.roles.viewer');
      default:
        return t('organizations.switcher.roles.member');
    }
  };

  const handleOrganizationChange = (newOrgId: string, role: string) => {
    // Ne rien faire si on change vers la même organisation
    if (newOrgId === currentOrganizationId) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
    setIsChanging(true);

    // Extraire le chemin actuel sans l'ID de l'organisation
    const pathSegments = location.pathname.split('/');
    const orgaIndex = pathSegments.indexOf('orga');

    let newPath: string;

    if (orgaIndex !== -1 && pathSegments[orgaIndex + 1]) {
      // Remplacer l'ID de l'organisation dans le chemin actuel
      const pathAfterOrga = pathSegments.slice(orgaIndex + 2).join('/');
      newPath = `/orga/${newOrgId}${pathAfterOrga ? `/${pathAfterOrga}` : ''}`;
    } else {
      // Si pas de chemin /orga trouvé, créer un chemin par défaut selon le rôle
      if (role === 'owner' || role === 'admin') {
        newPath = `/orga/${newOrgId}/admin`;
      } else {
        newPath = `/orga/${newOrgId}`;
      }
    }

    // Naviguer vers le nouveau chemin avec le même chemin relatif
    navigate(newPath);
  };

  // Réinitialiser l'état de chargement quand la route change
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChanging(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [location.pathname, setIsChanging]);

  // Vérifier si l'utilisateur a le droit de créer une organisation
  const canCreateOrg = isAdmin || affiliations.some(aff =>
    ['glowlive', 'biawear'].includes(aff.organizationId.toLowerCase())
  );


  // Afficher le chargement seulement si on charge les affiliations initiales
  // Une fois chargées, on peut afficher même si loadingOrgs est true (chargement des détails)
  if (loading && affiliations.length === 0) {
    return (
      <div
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${className}`}
        style={{
          backgroundColor: theme.colors.primary10 || '#EFF6FF',
          borderColor: theme.colors.primary30 || '#93C5FD',
        }}
      >
        <Building2 className="h-5 w-5" style={{ color: theme.colors.primary || '#2563EB' }} />
        <span className="text-sm font-medium" style={{ color: theme.colors.primary70 || '#1E40AF' }}>{t('organizations.switcher.loading')}</span>
      </div>
    );
  }

  // Si pas d'organisations après le chargement initial
  if (!loading && affiliations.length === 0 && !loadingOrgs) {
    return (
      <div
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${className}`}
        style={{
          backgroundColor: '#FEF3C7',
          borderColor: '#FCD34D',
        }}
      >
        <Building2 className="h-5 w-5" style={{ color: '#D97706' }} />
        <span className="text-sm font-medium" style={{ color: '#92400E' }}>{t('organizations.switcher.noOrganization')}</span>
      </div>
    );
  }

  // Utiliser les affiliations de base si les détails ne sont pas encore chargés
  const displayOrganizations = allOrganizations.length > 0
    ? allOrganizations
    : affiliationsWithIdorga.map(aff => ({
      ...aff,
      organizationInfo: {
        id: aff.idorga,
        name: aff.idorga,
        slug: aff.idorga,
      }
    }));

  const displayCurrentOrg = displayOrganizations.find(org =>
    org.idorga === currentOrganizationId
  ) || displayOrganizations[0];

  // Si on charge les détails mais qu'on a déjà des affiliations, afficher quand même
  if (loadingOrgs && allOrganizations.length === 0 && affiliations.length > 0) {
    // Continuer pour afficher avec les données de base
  }

  // Si vraiment aucune organisation
  if (displayOrganizations.length === 0 && !loading) {
    return (
      <div
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${className}`}
        style={{
          backgroundColor: '#FEF3C7',
          borderColor: '#FCD34D',
        }}
      >
        <Building2 className="h-5 w-5" style={{ color: '#D97706' }} />
        <span className="text-sm font-medium" style={{ color: '#92400E' }}>{t('organizations.switcher.noOrganization')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ overflow: 'visible', overflowX: 'visible' }}>
      {/* Bouton principal */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 min-w-[200px]"
        style={{
          backgroundColor: theme.colors.primary || '#1E293B',
          borderColor: theme.colors.primary30 || '#475569',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.primary20 || '#334155';
          e.currentTarget.style.borderColor = theme.colors.primary40 || '#64748B';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.primary || '#1E293B';
          e.currentTarget.style.borderColor = theme.colors.primary30 || '#475569';
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 2px ${theme.colors.primary30 || '#3B82F6'}, 0 0 0 4px rgba(59, 130, 246, 0.1)`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {displayCurrentOrg?.organizationInfo && 'logo' in displayCurrentOrg.organizationInfo && displayCurrentOrg.organizationInfo.logo ? (
          <img
            src={displayCurrentOrg.organizationInfo.logo}
            alt={displayCurrentOrg.organizationInfo.name}
            className="w-8 h-8 rounded-lg object-contain flex-shrink-0"
            style={{
              backgroundColor: theme.colors.background || '#FFFFFF',
            }}
          />
        ) : (
          <div
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
            style={{
              background: theme.colors.primary || '#3B82F6',
            }}
          >
            <Building2 className="h-4 w-4 text-white" />
          </div>
        )}
        <div className="flex flex-col items-start flex-1 min-w-0">
          <span className="text-sm font-semibold text-white truncate w-full text-left">
            {displayCurrentOrg?.organizationInfo?.name || displayCurrentOrg?.idorga || t('organizations.switcher.select')}
          </span>
          {displayCurrentOrg && (
            <span className="text-xs font-medium" style={{ color: theme.colors.backgroundTertiary || '#94A3B8' }}>
              {getRoleLabel(displayCurrentOrg.role)}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: theme.colors.backgroundTertiary || '#94A3B8' }}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay pour fermer le dropdown */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Menu dropdown */}
          <div
            ref={dropdownRef}
            className="fixed w-96 rounded-xl shadow-xl z-[9999] overflow-hidden"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              maxWidth: 'calc(100vw - 16px)',
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#E5E7EB',
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3 pt-2">
                {t('organizations.switcher.myOrganizations')}
              </div>

              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {displayOrganizations.map((org) => {
                  const isCurrent = org.idorga === currentOrganizationId;
                  return (
                    <button
                      key={org.idorga}
                      onClick={() => handleOrganizationChange(org.idorga, org.role)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 group border-l-2`}
                      style={{
                        backgroundColor: isCurrent
                          ? theme.colors.primary10 || '#EFF6FF'
                          : 'transparent',
                        borderLeftColor: isCurrent
                          ? theme.colors.primary || '#3B82F6'
                          : 'transparent',
                        boxShadow: isCurrent ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!isCurrent) {
                          e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrent) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {org.organizationInfo && 'logo' in org.organizationInfo && org.organizationInfo.logo ? (
                        <img
                          src={org.organizationInfo.logo}
                          alt={org.organizationInfo.name}
                          className="w-10 h-10 rounded-lg object-contain flex-shrink-0 shadow-sm"
                          style={{
                            backgroundColor: isCurrent
                              ? theme.colors.background || '#FFFFFF'
                              : theme.colors.backgroundTertiary || '#F3F4F6',
                          }}
                        />
                      ) : (
                        <div
                          className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 transition-colors"
                          style={{
                            backgroundColor: isCurrent
                              ? theme.colors.primary || '#3B82F6'
                              : theme.colors.backgroundTertiary || '#F3F4F6',
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrent) {
                              e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary || '#E5E7EB';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrent) {
                              e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                            }
                          }}
                        >
                          <Building2
                            className="h-5 w-5"
                            style={{
                              color: isCurrent
                                ? '#FFFFFF'
                                : theme.colors.foregroundSecondary || '#4B5563'
                            }}
                          />
                        </div>
                      )}
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span
                          className="text-sm font-semibold truncate w-full text-left"
                          style={{
                            color: isCurrent
                              ? theme.colors.primary || '#3B82F6'
                              : theme.colors.foreground || '#111827'
                          }}
                        >
                          {org.organizationInfo?.name || org.idorga}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {getRoleIcon(org.role)}
                          <span
                            className="text-xs font-medium"
                            style={{
                              color: isCurrent
                                ? theme.colors.primary || '#3B82F6'
                                : theme.colors.foregroundSecondary || '#6B7280'
                            }}
                          >
                            {getRoleLabel(org.role)}
                          </span>
                        </div>
                      </div>

                      {isCurrent && (
                        <div
                          className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: theme.colors.primary || '#3B82F6',
                          }}
                        >
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {displayOrganizations.length === 0 && (
                <div className="p-6 text-center">
                  <Building2
                    className="h-8 w-8 mx-auto mb-2"
                    style={{ color: theme.colors.foregroundSecondary || '#D1D5DB' }}
                  />
                  <p
                    className="text-sm"
                    style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}
                  >
                    {t('organizations.switcher.noOrganization')}
                  </p>
                </div>
              )}

              {/* Séparateur */}
              {displayOrganizations.length > 0 && (
                <div
                  className="border-t my-2"
                  style={{ borderColor: theme.colors.border || '#E5E7EB' }}
                ></div>
              )}

              {/* Bouton Créer une organisation */}
              {canCreateOrg && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/organizations/create');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 group"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F9FAFB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 transition-colors"
                    style={{
                      backgroundColor: theme.colors.primary || '#3B82F6',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary20 || '#2563EB';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary || '#3B82F6';
                    }}
                  >
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span
                      className="text-sm font-semibold truncate w-full text-left"
                      style={{ color: theme.colors.foreground || '#111827' }}
                    >
                      {t('organizations.switcher.createNew')}
                    </span>
                    <span
                      className="text-xs font-medium"
                      style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}
                    >
                      {t('organizations.switcher.newProject')}
                    </span>
                  </div>
                </button>
              )}

              {/* Bouton Mes organisations */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/organizations/manage');
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 group"
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 transition-colors"
                  style={{
                    backgroundColor: theme.colors.backgroundTertiary || '#F3F4F6',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary || '#E5E7EB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                  }}
                >
                  <Settings
                    className="h-5 w-5"
                    style={{ color: theme.colors.foregroundSecondary || '#4B5563' }}
                  />
                </div>
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span
                    className="text-sm font-semibold truncate w-full text-left"
                    style={{ color: theme.colors.foreground || '#111827' }}
                  >
                    {t('organizations.switcher.myOrganizations')}
                  </span>
                  <span
                    className="text-xs font-medium"
                    style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}
                  >
                    {t('organizations.switcher.manageAll')}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationSwitcher;


